import React, { useEffect, useState } from "react";
import { SearchModelEnum } from "types/qbank/searchModel";
import type { AutoSuggestValue } from "types/autoSuggestValue";
import {
  loadAutoSuggestValues,
  normalize,
} from "utilities/loadAutoSuggestValues";
import AutoSuggestDropdown from "./AutoSuggestDropdown";
import ActiveFilters from "./ActiveFilters";
import AdvancedSearchPanel from "./advanced/AdvancedSearchPanel";
import { SearchFilterLabels } from "types/searchFilterLabels";
import { SearchOperator } from "types/qbank/searchOperator";
import { useApi } from "hooks/api";
import { useFilter } from "hooks/filter";
import { useQuery } from "react-query";
import { MetaData } from "types/qbank/metadata";
import Dialog from "@mui/material/Dialog";
import Transition from "components/Transition";
import { Icon } from "components/ui";
import { PropertyCriteria } from "types/qbank/propertyCriteria";
import { AddSearchCriteria } from "types/addSearchCriteriaFunction";
import { Tooltip } from "@mui/material";

export default function SearchBar() {
  const { getMetaData } = useApi();
  const { searchState, searchDispatch } = useFilter();
  const [inputValue, setInputValue] = useState("");
  const [isActive, setActive] = useState(false);
  const [metaData, setMetaData] = useState<MetaData>({
    categories: {},
    deploymentSites: {},
    propertyTypes: {},
  });
  const [autoSuggestValues, setAutoSuggestValues] = useState<
    AutoSuggestValue[]
  >([]);
  const [currentlySuggestedValues, setCurrentlySuggestedValues] = useState<
    AutoSuggestValue[]
  >([]);
  const dropdownFocusRef: React.RefObject<HTMLAnchorElement> =
    React.createRef();
  const searchBarFocusRef: React.RefObject<HTMLInputElement> =
    React.createRef();

  function toggleAdvancedSearchPanel() {
    setActive(!isActive);
  }

  function clearSearchInput() {
    setInputValue("");
    setTimeout(() => searchBarFocusRef.current?.focus(), 1);
  }

  function addFreetextValue() {
    if (inputValue.trim() !== "") {
      searchDispatch({
        type: "ADD_FILTER",
        payload: {
          value: inputValue,
          label: "FreeText",
          criteria: SearchModelEnum.freeText,
        },
      });
    }
    clearSearchInput();
  }

  const { isLoading, isError, data } = useQuery("metaData", getMetaData, {
    staleTime: Infinity,
  });

  const transformFormat = (dataArray: AutoSuggestValue[]) => {
    const propertyCriteria: (PropertyCriteria | AddSearchCriteria)[] = [];

    if (dataArray === undefined)
      return propertyCriteria;

    dataArray.forEach((item: AutoSuggestValue) => {
      if (item.searchCriteria === SearchModelEnum.properties) {
        if (item.propertyCriteria !== undefined)
        propertyCriteria.push(item.propertyCriteria);
      }
      else {
        propertyCriteria.push({
          criteria: item.searchCriteria,
          propertyCriteria: item.propertyCriteria,
          value: item.value,
          overwrite: false,
          text: item.labelValue.toString(),
          label: item.label
            ? item.label
            : item.propertyCriteria
              ? item.label
              : item.labelValue.toString()
        })
      }
    });

    return propertyCriteria;
  };

  const getLocalStorageData = () => {
    const localStorageData = localStorage.getItem('SearchState');
    localStorage.removeItem('SearchState');
    if (localStorageData) {
      // Parse the JSON string back into an array of MyType
      const parsedLocalStorageData = JSON.parse(localStorageData);
      return parsedLocalStorageData
    }
    return null;
  };

  const getFilters = (localStorageData: any) => {

    if (localStorageData) {
      return transformFormat(localStorageData);
    }
    return [];
  };

  function isPropertyCriteria(obj: any): obj is PropertyCriteria {
    return (obj as PropertyCriteria).systemName !== undefined;
  }

  function isAddSearchCriteria(obj: any): obj is AddSearchCriteria {
    return (obj as AddSearchCriteria).criteria !== undefined;
  }

  useEffect(() => {
    const localStorageData = getLocalStorageData();
    const filters = getFilters(localStorageData ? localStorageData.activeFilters : undefined);
    var propertyFilters = filters.filter(isPropertyCriteria);
    var searchFilters = filters.filter(isAddSearchCriteria);
    if (localStorageData) {
      searchDispatch({
        type: "CHANGE_CATEGORY",
        payload: localStorageData.currentCategory,
      });
      if (propertyFilters && propertyFilters.length > 0) {
        searchDispatch({
          type: "ADD_FILTER",
          payload: {
            criteria: SearchModelEnum.properties,
            value: "",
            overwrite: true,
            propertyCriteria: propertyFilters,
            label: true,
          },
        });
      }
      if (searchFilters && searchFilters.length > 0) {
        searchFilters.forEach((filter) => {
          searchDispatch({
            type: "ADD_FILTER",
            payload: filter,
          });
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading && !isError && data) {
      setMetaData(data);
      //Parse metadata into autosuggest values
      if (Object.keys(data).length) {
        setAutoSuggestValues(loadAutoSuggestValues(data));
      }
    }
  }, [data, isError, isLoading]);

  useEffect(() => {
    function autoSuggest(input: string): AutoSuggestValue[] {
      const normalizedInputValue = normalize(input);

      let matchedValues: AutoSuggestValue[] = [];

      if (inputValue.length > 2 && autoSuggestValues.length) {
        matchedValues = autoSuggestValues.filter(
          (value) =>
            value.normalizedValue &&
            value.normalizedValue.indexOf(normalizedInputValue) > -1
        );
      }

      if (parseInt(input).toString() === input) {
        //Add numeric options
        matchedValues.push({
          searchCriteria: SearchModelEnum.mediaIds,
          searchOperator: SearchOperator.CONTAINS_ANY,
          label: SearchFilterLabels[SearchModelEnum.mediaIds],
          labelValue: input,
          value: input,
        });
        matchedValues.push({
          searchCriteria: SearchModelEnum.objectIds,
          searchOperator: SearchOperator.CONTAINS_ANY,
          label: SearchFilterLabels[SearchModelEnum.objectIds],
          labelValue: input,
          value: input,
        });
      }

      //Always add filename & title
      matchedValues.push({
        searchCriteria: SearchModelEnum.fileName,
        searchOperator: SearchOperator.LIKE,
        label: SearchFilterLabels[SearchModelEnum.fileName],
        labelValue: input,
        value: input,
      });
      matchedValues.push({
        searchCriteria: SearchModelEnum.name,
        searchOperator: SearchOperator.LIKE,
        label: SearchFilterLabels[SearchModelEnum.name],
        labelValue: input,
        value: input,
      });

      return matchedValues;
    }

    //Find autosuggest values while typing
    setCurrentlySuggestedValues(autoSuggest(inputValue));
  }, [inputValue, autoSuggestValues]);

  if (isError) {
    return null;
  }

  return (
    <div className="search">
      <div className="searchInputs">
        <div className="advance-search">
          <div className={`search-container dropdown ${inputValue.length ? "is-active" : ""}`}>
            <input
              ref={searchBarFocusRef}
              className="search-input"
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              tabIndex={0}
              onKeyUp={(e) => {
                if (e.code === "Enter" || e.code === "NumpadEnter") {
                  addFreetextValue();
                } else if (e.code === "ArrowDown") {
                  dropdownFocusRef.current?.focus();
                }
              }}
              placeholder="Search..."
            />
            <div className="dropdown-menu">
              {currentlySuggestedValues.length > 0 && (
                <AutoSuggestDropdown
                  focusRef={dropdownFocusRef}
                  values={currentlySuggestedValues}
                  onClose={clearSearchInput}
                />
              )}
            </div>
            <div className="search_icon" onClick={addFreetextValue} aria-label="Search">
              <Tooltip title="Apply filters" placement="top">
              {/* Search Icon */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <g clipPath="url(#clip0_522_381)">
                  <path
                    d="M10.5 18C14.6421 18 18 14.6421 18 10.5C18 6.35786 14.6421 3 10.5 3C6.35786 3 3 6.35786 3 10.5C3 14.6421 6.35786 18 10.5 18Z"
                    stroke="#023E58"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15.8035 15.8035L21 21"
                    stroke="#023E58"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_522_381">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              </Tooltip>
            </div>
          </div>

          {searchState.currentCategory.propertyRequests.length > 0 && (
            <Tooltip title="Add Filters">
            <button
              className="advanced-search-button"
              onClick={toggleAdvancedSearchPanel}
              aria-label="Advanced Search"
              type="button"
            >
              <Icon name="sliders-h" />
            </button>
            </Tooltip>
          )}
        </div>
      </div>
      <ActiveFilters />
      <Dialog
        fullScreen
        open={isActive}
        onClose={() => setActive(false)}
        TransitionComponent={Transition}
      >
        <AdvancedSearchPanel
          active={true}
          closePanel={() => setActive(false)}
          propertyTypes={metaData.propertyTypes}
        />
      </Dialog>
    </div >
  );
}
